import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Empty,
  Modal,
  Select,
  Spin,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import moment from "moment";
import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import LocalAssetsViewer from "@/v1/components/common/LocalAssetsViewer";
import { downloadUsingAnchorElement } from "@/v1/utils/download";
import { acceptFor, isAcceptedFile, PreviewType, previewTypeOf } from "./files";
import {
  DeleteDialogText,
  Field,
  FieldAction,
  FieldHeader,
  FieldName,
  FileList,
  FileRow,
  GroupCard,
  GroupTitle,
  Panel,
  Preview,
  PREVIEW_HEIGHT,
  PreviewLoading,
  VisuallyHidden,
} from "./styles";
import {
  DELETION_REASONS,
  DeletionReason,
  DOCUMENT_FIELDS,
  DocumentGroup,
  DocumentType,
  LeadDocument,
} from "./types";

const DATE_FORMAT = "DD.MM.YYYY";

const GROUPS = DOCUMENT_FIELDS.reduce<DocumentGroup[]>(
  (groups, { group }) => (groups.includes(group) ? groups : [...groups, group]),
  []
);

/**
 * One file's preview, open below its row: the shared LocalAssetsViewer, so a
 * PDF shows in the browser's own embedded viewer and an image gets the pan,
 * zoom and rotate toolbar, as in the customer Documents tab.
 */
const FilePreview = ({
  id,
  documentId,
  fileName,
  type,
  url,
}: {
  id: string;
  documentId: string;
  fileName: string;
  type: PreviewType;
  /** `undefined` while the signed URL is being fetched. */
  url?: string;
}) => {
  const { t } = useTranslation("Onboarding");
  const assets = useMemo(
    () => (url ? [{ id: documentId, fullsize: url, filetype: type }] : []),
    [documentId, type, url]
  );
  return (
    <Preview
      id={id}
      role="region"
      aria-label={String(t("documents.preview", { fileName }))}
    >
      {url ? (
        <LocalAssetsViewer
          id={`${id}-viewer`}
          assets={assets}
          minHeight={PREVIEW_HEIGHT}
          maxHeight={PREVIEW_HEIGHT}
        />
      ) : (
        <PreviewLoading role="status">
          <Spin aria-hidden="true" />
          <VisuallyHidden>{t("documents.previewLoading")}</VisuallyHidden>
        </PreviewLoading>
      )}
    </Preview>
  );
};

export interface LeadDocumentsProps {
  /** The lead's public id, which keys its documents in smarta-documents. */
  leadId: string;
  loadDocuments: (leadId: string) => Promise<LeadDocument[]>;
  uploadDocument: (
    leadId: string,
    documentType: DocumentType,
    file: File
  ) => Promise<void>;
  deleteDocument: (
    leadId: string,
    documentId: string,
    deletionReason: DeletionReason
  ) => Promise<void>;
  getFileUrl: (leadId: string, documentId: string) => Promise<string>;
  /**
   * Operator id to display name, for naming each file's uploader. A failure
   * or an unknown id shows the upload date alone.
   */
  loadOperatorNames: () => Promise<ReadonlyMap<string, string>>;
  /** Called after a file was stored, so the list can refresh its count. */
  onUploaded?: (leadId: string) => void;
  /** Called after a file was deleted, so the list can refresh its count. */
  onDeleted?: (leadId: string) => void;
}

/**
 * The nine document fields of one lead, shown when its row in the Onboarding
 * list is expanded. Clicking a PDF or an image opens its preview right below
 * its row, one at a time; a bank CSV, or anything the viewer cannot show,
 * downloads instead.
 */
export const LeadDocuments = ({
  leadId,
  loadDocuments,
  uploadDocument,
  deleteDocument,
  getFileUrl,
  loadOperatorNames,
  onUploaded,
  onDeleted,
}: LeadDocumentsProps) => {
  const { t } = useTranslation("Onboarding");
  const deleteReasonId = `lead-${leadId}-delete-reason`;
  /* `undefined` until the first load answers. */
  const [documents, setDocuments] = useState<LeadDocument[]>();
  const [error, setError] = useState(false);
  const [uploadingType, setUploadingType] = useState<DocumentType>();
  const [operatorNames, setOperatorNames] = useState<
    ReadonlyMap<string, string>
  >(new Map());
  /* The open preview; `url` is unset while its signed URL is fetched. */
  const [preview, setPreview] = useState<{
    documentId: string;
    url?: string;
  }>();
  /*
   * The file the delete dialog asks about. It stays set while the dialog
   * closes, so the dialog does not go blank during its close animation.
   */
  const [deleteTarget, setDeleteTarget] = useState<LeadDocument>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletionReason, setDeletionReason] = useState<DeletionReason>();
  const [deleting, setDeleting] = useState(false);
  /*
   * Only the latest load may write the list, and nothing writes after the
   * row collapsed (the component unmounted).
   */
  const latestLoad = useRef(0);
  const mounted = useRef(true);
  const uploadInFlight = useRef(false);
  const loaded = useRef(false);
  /* Only the latest click may write the preview. */
  const latestPreview = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /*
   * Never throws. A reload after an upload keeps the list on screen while it
   * runs, and a failure then is a message, not an empty panel: the list shown
   * is still true, it only misses the new file.
   */
  const reload = useCallback(async () => {
    const request = ++latestLoad.current;
    const isCurrent = () => mounted.current && latestLoad.current === request;
    setError(false);
    try {
      const result = await loadDocuments(leadId);
      if (!isCurrent()) return;
      loaded.current = true;
      setDocuments(result);
    } catch {
      if (!isCurrent()) return;
      if (loaded.current) void message.error(t("documents.error"));
      else setError(true);
    }
  }, [leadId, loadDocuments, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /* The names fill in when they arrive; the list never waits for them. */
  useEffect(() => {
    let active = true;
    loadOperatorNames()
      .then((names) => active && setOperatorNames(names))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [loadOperatorNames]);

  const documentsByType = useMemo(() => {
    const byType = new Map<DocumentType, LeadDocument[]>();
    for (const document of documents ?? []) {
      byType.set(document.documentType, [
        ...(byType.get(document.documentType) ?? []),
        document,
      ]);
    }
    return byType;
  }, [documents]);

  /* Always returns false: antd must not send the file itself. */
  const upload = (documentType: DocumentType, file: File) => {
    if (uploadInFlight.current) return false;
    if (!isAcceptedFile(documentType, file)) {
      void message.error(t("documents.invalidFile"));
      return false;
    }
    uploadInFlight.current = true;
    setUploadingType(documentType);
    void (async () => {
      try {
        await uploadDocument(leadId, documentType, file);
        if (!mounted.current) return;
        void message.success(t("documents.uploadSuccess"));
        onUploaded?.(leadId);
        await reload();
      } catch {
        if (mounted.current) void message.error(t("documents.uploadError"));
      } finally {
        uploadInFlight.current = false;
        if (mounted.current) setUploadingType(undefined);
      }
    })();
    return false;
  };

  const askToDelete = (document: LeadDocument) => {
    setDeleteTarget(document);
    setDeletionReason(undefined);
    setDeleteDialogOpen(true);
  };

  /*
   * The row leaves the list already on screen, and the reload behind it
   * invalidates any older load still in flight, which could otherwise put the
   * deleted row back. On failure the dialog stays open with the file still
   * listed, so the operator can try again or cancel.
   */
  const confirmDelete = async () => {
    if (!deleteTarget || !deletionReason || deleting) return;
    const { documentId } = deleteTarget;
    setDeleting(true);
    try {
      await deleteDocument(leadId, documentId, deletionReason);
      if (!mounted.current) return;
      setDocuments((current) =>
        current?.filter((document) => document.documentId !== documentId)
      );
      if (preview?.documentId === documentId) {
        /* Drop the deleted file's preview, and any URL fetch still behind it. */
        latestPreview.current++;
        setPreview(undefined);
      }
      void reload();
      setDeleteDialogOpen(false);
      void message.success(t("documents.delete.success"));
      onDeleted?.(leadId);
    } catch {
      if (mounted.current) void message.error(t("documents.delete.error"));
    } finally {
      if (mounted.current) setDeleting(false);
    }
  };

  /*
   * The signed URL is short-lived, so it is fetched when a preview opens, not
   * ahead for every file. Clicking the open file again closes its preview.
   */
  const togglePreview = async (document: LeadDocument) => {
    const request = ++latestPreview.current;
    const isCurrent = () =>
      mounted.current && latestPreview.current === request;
    if (preview?.documentId === document.documentId) {
      setPreview(undefined);
      return;
    }
    setPreview({ documentId: document.documentId });
    try {
      const url = await getFileUrl(leadId, document.documentId);
      if (isCurrent()) setPreview({ documentId: document.documentId, url });
    } catch {
      if (!isCurrent()) return;
      setPreview(undefined);
      void message.error(t("documents.openError"));
    }
  };

  /* A CSV, or a file the viewer cannot show, downloads. */
  const download = async (document: LeadDocument, event: MouseEvent) => {
    try {
      const url = await getFileUrl(leadId, document.documentId);
      downloadUsingAnchorElement({ url, fileName: document.fileName, event });
    } catch {
      void message.error(t("documents.openError"));
    }
  };

  const meta = (document: LeadDocument) => {
    const date = moment(document.uploadedAt).format(DATE_FORMAT);
    const name = operatorNames.get(document.uploadedByOperatorId);
    return name
      ? t("documents.uploadedAtBy", { date, name })
      : t("documents.uploadedAt", { date });
  };

  if (error) {
    return (
      <Empty description={t("documents.error")}>
        <Button onClick={() => void reload()}>{t("documents.retry")}</Button>
      </Empty>
    );
  }

  if (!documents) {
    return (
      <Panel>
        <Spin aria-label={String(t("documents.loading"))} />
      </Panel>
    );
  }

  return (
    <Panel>
      {GROUPS.map((group) => (
        <GroupCard
          key={group}
          aria-label={String(t(`documents.groups.${group}`))}
        >
          <GroupTitle>{t(`documents.groups.${group}`)}</GroupTitle>
          {DOCUMENT_FIELDS.filter((field) => field.group === group).map(
            ({ type }) => {
              const files = documentsByType.get(type) ?? [];
              const labelId = `lead-${leadId}-documents-${type}`;
              return (
                <Field key={type} role="group" aria-labelledby={labelId}>
                  <FieldHeader>
                    <FieldName id={labelId}>
                      {t(`documents.fields.${type}`)}
                    </FieldName>
                    <Tag>
                      {files.length
                        ? t("documents.fileCount", { count: files.length })
                        : t("documents.nothingYet")}
                    </Tag>
                    <FieldAction>
                      <Upload
                        accept={acceptFor(type)}
                        aria-label={String(t("documents.upload"))}
                        beforeUpload={(file) => upload(type, file)}
                        disabled={Boolean(uploadingType)}
                        showUploadList={false}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          disabled={Boolean(uploadingType)}
                          loading={uploadingType === type}
                        >
                          {uploadingType === type
                            ? t("documents.uploading")
                            : t("documents.upload")}
                        </Button>
                      </Upload>
                    </FieldAction>
                  </FieldHeader>
                  {files.length > 0 && (
                    <FileList>
                      {files.map((document) => {
                        const previewType = previewTypeOf(document);
                        const previewId = `lead-${leadId}-preview-${document.documentId}`;
                        const open =
                          preview?.documentId === document.documentId;
                        return (
                          <FileRow key={document.documentId}>
                            {previewType ? (
                              <Button
                                type="link"
                                title={document.fileName}
                                aria-expanded={open}
                                aria-controls={open ? previewId : undefined}
                                onClick={() => void togglePreview(document)}
                              >
                                {document.fileName}
                              </Button>
                            ) : (
                              <Button
                                type="link"
                                title={document.fileName}
                                aria-label={String(
                                  t("documents.download", {
                                    fileName: document.fileName,
                                  })
                                )}
                                onClick={(event) =>
                                  void download(document, event)
                                }
                              >
                                {document.fileName}
                              </Button>
                            )}
                            <Typography.Text type="secondary">
                              {meta(document)}
                            </Typography.Text>
                            <Button
                              type="text"
                              size="small"
                              icon={<DeleteOutlined />}
                              title={String(t("documents.delete.action"))}
                              aria-label={String(
                                t("documents.delete.actionFor", {
                                  fileName: document.fileName,
                                })
                              )}
                              onClick={() => askToDelete(document)}
                            />
                            {previewType && open && (
                              <FilePreview
                                id={previewId}
                                documentId={document.documentId}
                                fileName={document.fileName}
                                type={previewType}
                                url={preview?.url}
                              />
                            )}
                          </FileRow>
                        );
                      })}
                    </FileList>
                  )}
                </Field>
              );
            }
          )}
        </GroupCard>
      ))}
      <Modal
        open={deleteDialogOpen}
        title={t("documents.delete.title")}
        okText={t("documents.delete.confirm")}
        cancelText={t("documents.delete.cancel")}
        okButtonProps={{
          danger: true,
          disabled: !deletionReason,
          loading: deleting,
        }}
        cancelButtonProps={{ disabled: deleting }}
        closable={!deleting}
        keyboard={!deleting}
        maskClosable={!deleting}
        onOk={() => void confirmDelete()}
        onCancel={() => setDeleteDialogOpen(false)}
        afterClose={() => setDeleteTarget(undefined)}
      >
        <DeleteDialogText>
          {t("documents.delete.text", { fileName: deleteTarget?.fileName })}
        </DeleteDialogText>
        <label htmlFor={deleteReasonId}>{t("documents.delete.reason")}</label>
        <Select<DeletionReason>
          id={deleteReasonId}
          value={deletionReason}
          placeholder={t("documents.delete.reasonPlaceholder")}
          onChange={setDeletionReason}
          disabled={deleting}
          options={DELETION_REASONS.map((reason) => ({
            value: reason,
            label: t(`documents.delete.reasons.${reason}`),
          }))}
          style={{ display: "block", marginTop: 4 }}
        />
      </Modal>
    </Panel>
  );
};
